/* eslint-disable prefer-const */
import { useCallback } from 'react';

import { useLatestValue } from '@/utils/hooks/useControllable';

import { useBoolean } from './useBoolean';
import { useCounter } from './useCounter';
import { useInterval } from './useInterval';

interface CountdownOption {
  countStart: number;
  intervalMs?: number;
  isIncrement?: boolean;
  countStop?: number;
  onEnd?: () => void;
  endOn?: number;
}

interface CountdownControllers {
  startCountdown: () => void;
  stopCountdown: () => void;
  resetCountdown: () => void;
  skipCountdownToEnd: () => void;
}

/**
 * @param  {CountdownOption} countdownOption
 * @param  {number} countdownOption.countStart - the countdown's starting number, initial value of the returned number.
 * @param  {?number} countdownOption.countStop -  `0` by default, the countdown's stopping number. Pass `-Infinity` to decrease forever.
 * @param  {?number} countdownOption.intervalMs - `1000` by default, the countdown's interval, in milliseconds.
 * @param  {?boolean} countdownOption.isIncrement - `false` by default, true if the countdown is increment.
 * @returns [counter, CountdownControllers]
 */
export function useCountdown(
  countdownOption: CountdownOption
): [number, CountdownControllers] {
  let countStart,
    intervalMs,
    isIncrement: boolean | undefined,
    countStop: number | undefined,
    endOn: number | undefined,
    onEnd: (() => void) | undefined;

  ({ countStart, intervalMs, isIncrement, countStop, onEnd, endOn } =
    countdownOption);

  const onEndRef = useLatestValue(onEnd);

  // default values
  intervalMs = intervalMs ?? 1000;
  isIncrement = isIncrement ?? false;
  countStop = countStop ?? 0;
  endOn = endOn ?? countStop;

  const {
    count,
    increment,
    decrement,
    reset: resetCounter,
    jumpCounterTo,
  } = useCounter(countStart);

  /**
   * Note: used to control the useInterval
   * running: If true, the interval is running
   * start: Should set running true to trigger interval
   * stop: Should set running false to remove interval
   */
  const {
    value: isCountdownRunning,
    setTrue: startCountdown,
    setFalse: stopCountdown,
  } = useBoolean(false);

  /**
   * Will set running false and reset the seconds to initial value
   */
  const resetCountdown = () => {
    stopCountdown();
    resetCounter();
  };

  const countdownCallback = useCallback(() => {
    if (count === endOn) {
      onEndRef.current?.();
    }

    if (count === countStop) {
      stopCountdown();
      return;
    }

    if (isIncrement) {
      increment();
    } else {
      decrement();
    }
  }, [
    count,
    countStop,
    decrement,
    endOn,
    increment,
    isIncrement,
    onEndRef,
    stopCountdown,
  ]);

  useInterval(countdownCallback, isCountdownRunning ? intervalMs : null);

  const skipCountdownToEnd = () => {
    jumpCounterTo(endOn);
  };

  return [
    count,
    {
      startCountdown,
      stopCountdown,
      resetCountdown,
      skipCountdownToEnd,
    } as CountdownControllers,
  ];
}
